import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

interface PageHeader {
    title?: string
    subtitle?: string
    action?: ReactNode
    leading?: ReactNode
}

interface Ctx {
    header: PageHeader
    setHeader: (h: PageHeader) => void
}

const BarberPageHeaderContext = createContext<Ctx | null>(null)

export function BarberPageHeaderProvider({ children }: { children: ReactNode }) {
    const [header, setHeader] = useState<PageHeader>({})
    return (
        <BarberPageHeaderContext.Provider value={{ header, setHeader }}>
            {children}
        </BarberPageHeaderContext.Provider>
    )
}

export function useBarberPageHeaderValue(): PageHeader {
    const ctx = useContext(BarberPageHeaderContext)
    return ctx?.header ?? {}
}

export function useBarberPageHeader(h: PageHeader) {
    const ctx = useContext(BarberPageHeaderContext)
    // `action` / `leading` are JSX and never reference-equal across renders —
    // depending on them re-fires setHeader every render and loops the provider
    // ("Maximum update depth exceeded"). Read them from a ref instead and
    // re-sync only when the primitive fields change.
    const hRef = useRef(h)
    hRef.current = h
    useEffect(() => {
        if (!ctx) return
        ctx.setHeader(hRef.current)
        return () => ctx.setHeader({})
    }, [h.title, h.subtitle]) // eslint-disable-line react-hooks/exhaustive-deps
}
